import { Injectable, OnDestroy, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from "@angular/common";
import { Subject } from 'rxjs';

import { Marked } from 'marked';
import markedAlert from 'marked-alert';
import { CerMermaid } from './cer-mermaid';
import { CerEmojiService } from "../cer-emoji/cer-emoji.service";

import { CerRandom } from '@util/cer-random';
import { CerLink } from './cer-link';
//import { CerMarkdownEdit } from './cer-markdown-edit';

export const ClassCerLink: string = 'cer-link';
export const Click = 'click';
export const KeyDown = 'keydown';
export const KeyUp = 'keyup';

@Injectable()
export class CerMarkdownService implements OnDestroy {

    private id: string = CerRandom.AsString(10);
    public linksClicked$: Subject<CerLink[]> = new Subject<CerLink[]>();
    private linkClick = this.onMarkedLinkClicked.bind(this);
    private keyUpDown = this.onKeyUpDown.bind(this);

    constructor(
        @Inject(PLATFORM_ID) private platformId: object,
        private emoji: CerEmojiService) {

        //window.addEventListener(MarkedClickEvent, this.linkClick, false);
        window.addEventListener(KeyDown, this.keyUpDown, false);
        window.addEventListener(KeyUp, this.keyUpDown, false);
        window.addEventListener(Click, this.linkClick, false);
    }

    ngOnDestroy(): void {
        window.removeEventListener(Click, this.linkClick, false);
        window.removeEventListener(KeyUp, this.keyUpDown, false);
        window.removeEventListener(KeyDown, this.keyUpDown, false);
        if (this.mermaid != null) {
            this.mermaid.linksClicked.unsubscribe();
            this.mermaid.dispose();
        }
        //if (this.markDownEdit != null) {
        //    this.markDownEdit.destroy();
        //}
    }

    /*
    private markDownEdit: CerMarkdownEdit;
    private editToDiv(markdown: string, div: HTMLDivElement) {
        this.markDownEdit ??= new CerMarkdownEdit();
        this.markDownEdit.editDocument(div, markdown);
    }
    */

    public parse(markdown: string): string {
        const marked = this.markedGet();
        markdown = this.emoji.transform(markdown);
        markdown = CerMermaid.linkRefine(markdown, this.id);
        var md = marked?.parse(markdown) as string;
        return this.handleInternalLink(md);
    }

    private handleInternalLink(md: string): string {
        return md;
    }

    private marked: Marked<string, string> = null;
    private markedGet(): Marked<string, string> {
        if (this.marked != null) {
            return this.marked;
        }
        else {
            this.marked = CerMarkdownService.markedCreate(this.id);
            return this.marked;
        }
    }

    public static markedCreate(id: string): Marked<string, string> {
        const marked = new Marked();
        marked.use({
            breaks: true,
            renderer: {
                code: (code) => {
                    if (code.lang !== 'mermaid') {
                        return `<pre><code>${code.text}</code></pre>`;
                    }
                    // Use Mermaid to render the diagram
                    return `<div><pre class="mermaid">${code.text}</pre></div>`;
                },
                heading({ text, depth }) {
                    return `<h${depth}>${text}</h${depth}>`;
                },
                link({ tokens, href, text }) {
                    return `<a class="${ClassCerLink} ${ClassCerLink}-${id}" href="${href}" >${text}</a>`;
                },
            }
        });
        marked.use(markedAlert());
        return marked;
    }

    // Mermaid
    private mermaid: CerMermaid;
    private mermaidIsLoading = false;
    public refreshElement(div: HTMLDivElement) {
        if (window == null || !isPlatformBrowser(this.platformId)) {
            return;
        }
        (<any>div.dataset).cerMarkdownId = this.id;
        if (CerMermaid.canRefresh(div)) {
            if (this.mermaid) {
                this.mermaid.run();
            }
            else if (!this.mermaidIsLoading) {
                this.mermaidIsLoading = true;
                CerMermaid.createAndRun(this.id).then(mermaid => {
                    mermaid.linksClicked.subscribe(cerLinks => this.linkClickedNext(cerLinks));
                    this.mermaid = mermaid;
                });
            }
        }
    }

    // Links:
    public onMarkedLinkClicked(e: MouseEvent) {
        if (e.target && e.target instanceof HTMLAnchorElement) {
            const a: HTMLAnchorElement = e.target;
            if (a.classList.contains(`${ClassCerLink}-${this.id}`)) {
                e.stopPropagation();
                e.preventDefault();
                this.linkClicked([a.href], a.innerText, this.id);
            }
        }
    }

    private linkClicked(hrefs: string[], title: string, id: string) {
        const links: CerLink[] = [];
        for (const href of hrefs) {
            const link = new CerLink();
            link.title = title;
            link.url = href;
            link.id = id;
            links.push(link);
        }
        this.linkClickedNext(links);
    }

    private shiftKey: boolean = false; // Open link as "doc:"
    private ctrlKey: boolean = false; // Open link as "app:"
    public onKeyUpDown(e: KeyboardEvent) {
        this.shiftKey = e.shiftKey;
        this.ctrlKey = e.ctrlKey;
    }

    private linkClickedNext(links: CerLink[]) {
        links.forEach(link => {
            link.init(this.shiftKey, this.ctrlKey);
        });
        this.linksClicked$.next(links);
    }

}
